import { useEffect, useMemo, useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import {
  ArrowLeft,
  BarChart3,
  ChevronDown,
  Euro,
  FileText,
  List,
  MoreVertical,
  Percent,
  RotateCcw,
  Sliders,
  TrendingUp,
} from "lucide-react";
import { Button, SearchIcon, SelectField } from "@fawkes/ui";

import { formatDate as formatDatum, formatEuro } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";
import { PeriodeFilter, periodeBereik } from "../components/PeriodeFilter";

/**
 * Financieel → Omzet (maintainer design 2026-08-17): turnover as the bookkeeping reads it — per
 * grootboekrekening, with the branch, the module, the excl./incl. BTW split and the number of bookings, plus
 * the month trend beside it.
 *
 * <p>Clicking an account drills into it: the same aggregate filtered to that account, shown as the full
 * trend ("Bekijk volledige analyse" does the same for the current filter). That is why the api returns the
 * rows, the month series and the totals in one payload — a drill-through must not change what the numbers
 * mean, only which set they describe.
 *
 * <p>Amounts come off the invoice LINES ({@code factuur_regel.grootboek_rekening}), because an invoice can
 * carry lines on several accounts; see the api's {@code OmzetQueryService} for the BTW split.
 */

interface OmzetRegel {
  sleutel: string;
  omschrijving: string | null;
  vestiging: string | null;
  onderdeel: string | null;
  exclBtw: number;
  btw: number;
  inclBtw: number;
  aantal: number;
}

interface MaandPunt {
  maand: string;
  exclBtw: number;
  btw: number;
  inclBtw: number;
  aantal: number;
}

interface OmzetPayload {
  rijen: OmzetRegel[];
  perMaand: MaandPunt[];
  totalen: { inclBtw: number; exclBtw: number; btw: number; aantal: number; gemiddelde: number };
  rekeningen: { nummer: string; omschrijving: string | null }[];
}

/** The api's group for lines without a ledger account — spelled out on screen. */
const ZONDER_REKENING = "(geen)";

/** Bookings page size — a revenue account can hold a hundred thousand lines. */
const BOEKINGEN_PER_PAGINA = 25;

interface Boeking {
  regelId: number | null;
  factuurId: number | null;
  factuurNummer: string | null;
  datum: string | null;
  klantNaam: string | null;
  productNaam: string | null;
  omschrijving: string | null;
  grootboek: string | null;
  grootboekOmschrijving: string | null;
  vestiging: string | null;
  onderdeel: string | null;
  aantal: number | null;
  exclBtw: number;
  btw: number;
  inclBtw: number;
}

interface BoekingenPage {
  items: Boeking[];
  total: number;
  page: number;
  size: number;
}

type Groepering = "grootboek" | "vestiging" | "onderdeel" | "maand";
type Sortering = "hoogste" | "laagste" | "boekingen" | "sleutel";
type Weergave = "beide" | "excl" | "incl";

export function OmzetOverzicht({ onOpenFactuur }: { onOpenFactuur?: (id: number) => void } = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [periode, setPeriode] = useState("maand");
  const [eigenPeriode, setEigenPeriode] = useState({ van: "", tot: "" });
  const [onderdeel, setOnderdeel] = useState("");
  const [grootboek, setGrootboek] = useState("");
  const [groeperen, setGroeperen] = useState<Groepering>("grootboek");
  const [sortering, setSortering] = useState<Sortering>("hoogste");
  const [weergave, setWeergave] = useState<Weergave>("beide");
  const [alleenMetBoekingen, setAlleenMetBoekingen] = useState(true);
  const [vestigingen, setVestigingen] = useState<string[]>([]);
  const [vestigingOpen, setVestigingOpen] = useState(false);
  const [vestigingZoek, setVestigingZoek] = useState("");
  const [analyse, setAnalyse] = useState(false);
  const [menuVoor, setMenuVoor] = useState<string | null>(null);
  // Which group's bookings are open ("56 boekingen" → exactly those 56 lines), and its page.
  const [boekingenVoor, setBoekingenVoor] = useState<string | null>(null);
  const [boekingenPagina, setBoekingenPagina] = useState(1);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  const bereik = useMemo(() => periodeBereik(periode, new Date(), eigenPeriode), [periode, eigenPeriode]);

  useEffect(() => {
    if (!vestigingOpen && menuVoor == null) return;
    const buiten = (e: MouseEvent) => {
      const doel = e.target as HTMLElement | null;
      if (doel?.closest(".fawkes-fin__multi, .fawkes-fin__rowmenu")) return;
      setVestigingOpen(false);
      setMenuVoor(null);
    };
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setVestigingOpen(false); setMenuVoor(null); }
    };
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", toets);
    };
  }, [vestigingOpen, menuVoor]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    if (bereik.from) params.set("from", bereik.from);
    if (bereik.to) params.set("to", bereik.to);
    vestigingen.forEach((v) => params.append("vestiging", v));
    if (onderdeel) params.set("onderdeel", onderdeel);
    if (grootboek) params.set("grootboek", grootboek);
    params.set("groeperen", groeperen);
    params.set("sort", sortering);
    return `facturen/omzet?${params.toString()}`;
  }, [debounced, bereik, vestigingen, onderdeel, grootboek, groeperen, sortering]);

  const { data: vestigingFeed } = useCustom<{ id: number; naam: string }[]>({
    url: "vestigingen", method: "get",
  });
  const alleVestigingen = useMemo(() => {
    const rijen = vestigingFeed?.data;
    const namen = Array.isArray(rijen) ? rijen.map((v) => (v?.naam ?? "").trim()).filter(Boolean) : [];
    return [...new Set(namen)].sort((a, b) => a.localeCompare(b));
  }, [vestigingFeed]);
  const zichtbareVestigingen = useMemo(() => {
    const naald = vestigingZoek.trim().toLowerCase();
    const gefilterd = naald
      ? alleVestigingen.filter((v) => v.toLowerCase().includes(naald))
      : alleVestigingen;
    const aan = gefilterd.filter((v) => vestigingen.includes(v));
    return [...aan, ...gefilterd.filter((v) => !vestigingen.includes(v))];
  }, [alleVestigingen, vestigingZoek, vestigingen]);

  const boekingenUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    if (bereik.from) params.set("from", bereik.from);
    if (bereik.to) params.set("to", bereik.to);
    vestigingen.forEach((v) => params.append("vestiging", v));
    if (onderdeel) params.set("onderdeel", onderdeel);
    if (grootboek) params.set("grootboek", grootboek);
    params.set("groeperen", groeperen);
    if (boekingenVoor) params.set("sleutel", boekingenVoor);
    params.set("page", String(boekingenPagina));
    params.set("size", String(BOEKINGEN_PER_PAGINA));
    return `facturen/omzet/boekingen?${params.toString()}`;
  }, [debounced, bereik, vestigingen, onderdeel, grootboek, groeperen, boekingenVoor, boekingenPagina]);

  const { data: boekingenData, isLoading: boekingenBezig } = useCustom<BoekingenPage>({
    url: boekingenUrl, method: "get", queryOptions: { enabled: boekingenVoor != null },
  });

  const { data, isLoading, isError, refetch } = useCustom<OmzetPayload>({ url, method: "get" });
  const payload = data?.data;
  const totalen = payload?.totalen ?? { inclBtw: 0, exclBtw: 0, btw: 0, aantal: 0, gemiddelde: 0 };
  const perMaand = payload?.perMaand ?? [];
  const rekeningen = payload?.rekeningen ?? [];
  // "Alleen omzet met boekingen": a group with nothing booked adds a zero row to a screen about money.
  const rijen = useMemo(
    () => (payload?.rijen ?? []).filter((r) => !alleenMetBoekingen || r.aantal > 0),
    [payload, alleenMetBoekingen],
  );

  const wissen = () => {
    setQuery(""); setPeriode("maand"); setOnderdeel(""); setGrootboek(""); setVestigingen([]);
    setGroeperen("grootboek"); setSortering("hoogste"); setWeergave("beide"); setAlleenMetBoekingen(true);
  };

  const boekingen = boekingenData?.data?.items ?? [];
  const boekingenTotaal = boekingenData?.data?.total ?? 0;
  const boekingenPaginas = Math.max(1, Math.ceil(boekingenTotaal / BOEKINGEN_PER_PAGINA));

  const gekozenRekening = rekeningen.find((r) => r.nummer === grootboek);

  return (
    <>
      <header className="fawkes-fin__head">
        <span className="fawkes-fin__head-icon" aria-hidden="true"><TrendingUp size={26} /></span>
        <div className="fawkes-fin__head-text">
          <h2>{t("omzet.title")}</h2>
          <p>{t("omzet.subtitle")}</p>
        </div>
        <div className="fawkes-fin__kpis">
          <Kpi icon={<Euro size={18} />} value={formatEuro(totalen.inclBtw, locale)} label={t("omzet.kpi.totaal")} />
          <Kpi icon={<FileText size={18} />} value={formatEuro(totalen.exclBtw, locale)} label={t("omzet.kpi.exclBtw")} />
          <Kpi icon={<Percent size={18} />} value={formatEuro(totalen.btw, locale)} label={t("omzet.kpi.btw")} />
          <Kpi icon={<List size={18} />} value={String(totalen.aantal)} label={t("omzet.kpi.boekingen")} />
          <Kpi icon={<TrendingUp size={18} />} value={formatEuro(totalen.gemiddelde, locale)}
            label={t("omzet.kpi.gemiddeld")} accent />
        </div>
      </header>

      {/* Six labelled filters on the first line, the display choice + wissen on the second. */}
      <section className="fawkes-fin__filters fawkes-fin__filters--zes">
        <div className="fawkes-fin__searchcell">
          <span className="fui-field__label" aria-hidden="true">&nbsp;</span>
          <label className="fawkes-fin__search">
            <span className="fawkes-fin__search-icon" aria-hidden="true">
              <SearchIcon width={18} height={18} />
            </span>
            <input
              value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t("omzet.filter.zoek")} aria-label={t("omzet.filter.zoek")}
            />
          </label>
        </div>
        <PeriodeFilter waarde={periode} onWaarde={setPeriode} eigen={eigenPeriode} onEigen={setEigenPeriode} />
        {alleVestigingen.length > 0 && (
          <div className="fawkes-fin__multi">
            <span className="fui-field__label">{t("financieel.filter.vestiging")}</span>
            <button
              type="button" className="fawkes-fin__multi-btn"
              aria-haspopup="true" aria-expanded={vestigingOpen}
              onClick={() => setVestigingOpen((v) => !v)}
            >
              <span>
                {vestigingen.length === 0
                  ? t("financieel.filter.alle")
                  : vestigingen.length === 1
                    ? vestigingen[0]
                    : t("financieel.filter.gekozen", { args: [String(vestigingen.length)] })}
              </span>
              <ChevronDown size={14} aria-hidden="true" />
            </button>
            {vestigingOpen && (
              <div className="fawkes-fin__multi-menu" role="group" aria-label={t("financieel.filter.vestiging")}>
                <label className="fawkes-fin__multi-zoek">
                  <SearchIcon width={14} height={14} aria-hidden="true" />
                  <input
                    value={vestigingZoek} autoFocus
                    placeholder={t("financieel.filter.vestigingZoek")}
                    aria-label={t("financieel.filter.vestigingZoek")}
                    onChange={(e) => setVestigingZoek(e.target.value)}
                  />
                </label>
                <button
                  type="button" className="fawkes-fin__multi-all"
                  onClick={() => setVestigingen((cur) =>
                    zichtbareVestigingen.every((v) => cur.includes(v))
                      ? cur.filter((v) => !zichtbareVestigingen.includes(v))
                      : [...new Set([...cur, ...zichtbareVestigingen])])}
                >
                  {zichtbareVestigingen.length > 0 && zichtbareVestigingen.every((v) => vestigingen.includes(v))
                    ? t("financieel.filter.allesUit")
                    : t("financieel.filter.allesAan")}
                </button>
                <div className="fawkes-fin__multi-list">
                  {zichtbareVestigingen.map((v) => (
                    <label key={v}>
                      <input
                        type="checkbox" checked={vestigingen.includes(v)}
                        onChange={() => setVestigingen((cur) =>
                          cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v])}
                      />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <SelectField
          label={t("financieel.filter.onderdeel")} value={onderdeel}
          onChange={(e) => setOnderdeel(e.target.value)}
          options={[
            { value: "", label: t("financieel.filter.alle") },
            { value: "Asiel", label: "Asiel" },
            { value: "Losse verkoop", label: "Losse verkoop" },
          ]}
        />
        <SelectField
          label={t("omzet.filter.grootboek")} value={grootboek}
          onChange={(e) => setGrootboek(e.target.value)}
          options={[{ value: "", label: t("financieel.filter.alle") },
            ...rekeningen.map((r) => ({
              value: r.nummer,
              label: r.omschrijving ? `${r.nummer} — ${r.omschrijving}` : r.nummer,
            }))]}
        />
        <SelectField
          label={t("omzet.filter.groeperen")} value={groeperen}
          onChange={(e) => setGroeperen(e.target.value as Groepering)}
          options={[
            { value: "grootboek", label: t("omzet.groep.grootboek") },
            { value: "vestiging", label: t("omzet.groep.vestiging") },
            { value: "onderdeel", label: t("omzet.groep.onderdeel") },
            { value: "maand", label: t("omzet.groep.maand") },
          ]}
        />
        <SelectField
          label={t("omzet.filter.weergave")} value={weergave}
          onChange={(e) => setWeergave(e.target.value as Weergave)}
          options={[
            { value: "beide", label: t("omzet.weergave.beide") },
            { value: "excl", label: t("omzet.weergave.excl") },
            { value: "incl", label: t("omzet.weergave.incl") },
          ]}
        />
        <div className="fawkes-fin__filters-actions">
          <span className="fui-field__label" aria-hidden="true">&nbsp;</span>
          <Button variant="secondary" onClick={wissen}>
            <RotateCcw size={14} /> {t("financieel.filter.wissen")}
          </Button>
        </div>
        <div className="fawkes-fin__toggle">
          <button
            type="button" role="switch" aria-checked={alleenMetBoekingen}
            className={`fawkes-fin__switch${alleenMetBoekingen ? " is-on" : ""}`}
            onClick={() => setAlleenMetBoekingen((v) => !v)}
          >
            <span className="fawkes-fin__switch-knob" />
          </button>
          <span className="fawkes-fin__toggle-label">{t("omzet.filter.alleenMetBoekingen")}</span>
          <span className="fawkes-fin__toggle-state">
            {t(alleenMetBoekingen ? "financieel.filter.actief" : "financieel.filter.inactief")}
          </span>
        </div>
      </section>

      <div className="fawkes-fin__resultbar">
        <span className="fawkes-fin__count">
          <Sliders size={14} aria-hidden="true" />
          {t("financieel.resultaten", { args: [String(rijen.length)] })}
          {grootboek && (
            <button type="button" className="fawkes-omzet__chip" onClick={() => setGrootboek("")}>
              {gekozenRekening?.omschrijving
                ? `${grootboek} — ${gekozenRekening.omschrijving}`
                : grootboek} ×
            </button>
          )}
        </span>
        <div className="fawkes-fin__resultbar-right">
          <Button variant="secondary" onClick={() => setAnalyse((v) => !v)}>
            {analyse ? <ArrowLeft size={14} /> : <BarChart3 size={14} />}{" "}
            {t(analyse ? "omzet.terug" : "omzet.analyse")}
          </Button>
          <label className="fawkes-fin__sort">
            <span>{t("financieel.sorteren")}</span>
            <select value={sortering} onChange={(e) => setSortering(e.target.value as Sortering)}>
              <option value="hoogste">{t("omzet.sorteren.hoogste")}</option>
              <option value="laagste">{t("omzet.sorteren.laagste")}</option>
              <option value="boekingen">{t("omzet.sorteren.boekingen")}</option>
              <option value="sleutel">{t("omzet.sorteren.sleutel")}</option>
            </select>
          </label>
        </div>
      </div>

      {isLoading && <p className="fawkes-fin__state">{t("common.loading")}</p>}
      {isError && (
        <p className="fawkes-fin__state fawkes-fin__state--error">
          {t("common.loadError")}{" "}
          <Button variant="secondary" onClick={() => refetch()}>{t("common.retry")}</Button>
        </p>
      )}

      {!isLoading && !isError && analyse && boekingenVoor == null && (
        <section className="fawkes-omzet__analyse">
          <h3>
            <TrendingUp size={16} aria-hidden="true" />{" "}
            {grootboek
              ? t("omzet.analyse.titelRekening", { args: [grootboek, gekozenRekening?.omschrijving ?? ""] })
              : t("omzet.analyse.titel")}
          </h3>
          <Trend punten={perMaand} locale={locale} groot />
          <IndeelbareTabel
            tabelKey="omzet-per-maand"
            columns={[
              { key: "maand", header: t("omzet.groep.maand"), cell: (p) => maandLabel(p.maand, locale) },
              { key: "aantal", header: t("omzet.kolom.aantal"),
                cell: (p) => <span className="is-num tabular">{p.aantal}</span> },
              { key: "exclBtw", header: t("omzet.kolom.exclBtw"),
                cell: (p) => <span className="is-num tabular">{formatEuro(p.exclBtw, locale)}</span> },
              { key: "btw", header: t("omzet.kolom.btw"),
                cell: (p) => <span className="is-num tabular">{formatEuro(p.btw, locale)}</span> },
              { key: "inclBtw", header: t("omzet.kolom.inclBtw"),
                cell: (p) => <span className="is-num tabular">{formatEuro(p.inclBtw, locale)}</span> },
              {
                key: "verschil", header: t("omzet.kolom.verschil"),
                // Het verschil is dat met de vórige maand in deze lijst, dus het hangt aan de positie
                // en niet aan de rij zelf.
                cell: (p) => {
                  const i = perMaand.indexOf(p);
                  const vorige = i > 0 ? perMaand[i - 1].inclBtw : null;
                  const verschil = vorige == null || vorige === 0 ? null
                    : ((p.inclBtw - vorige) / Math.abs(vorige)) * 100;
                  return (
                    <span className={`is-num tabular${verschil != null && verschil < 0 ? " is-danger" : ""}`}>
                      {verschil == null ? "—" : `${verschil > 0 ? "+" : ""}${verschil.toFixed(1)}%`}
                    </span>
                  );
                },
              },
            ]}
            rows={perMaand}
            getRowKey={(p) => String(p.maand)}
            empty=""
          />
        </section>
      )}

      {boekingenVoor != null && (
        <section className="fawkes-omzet__analyse">
          <h3>
            <List size={16} aria-hidden="true" />{" "}
            {t("omzet.boekingen.titel", {
              args: [boekingenVoor === ZONDER_REKENING ? t("omzet.zonderRekening") : boekingenVoor],
            })}
            <Button variant="secondary" onClick={() => setBoekingenVoor(null)}>
              <ArrowLeft size={14} /> {t("omzet.terug")}
            </Button>
          </h3>
          {boekingenBezig && <p className="fawkes-fin__state">{t("common.loading")}</p>}
          {!boekingenBezig && boekingen.length === 0 && (
            <p className="fawkes-fin__state">{t("omzet.boekingen.leeg")}</p>
          )}
          {!boekingenBezig && boekingen.length > 0 && (
            <>
              <IndeelbareTabel
                tabelKey="omzet-boekingen"
                columns={[
                  {
                    key: "factuur", header: t("betalingen.kolom.factuur"),
                    cell: (b) => (b.factuurId ? (
                      <button
                        type="button" className="fawkes-fin__link"
                        onClick={() => onOpenFactuur?.(b.factuurId as number)}
                      >
                        {b.factuurNummer || t("financieel.concept")}
                      </button>
                    ) : "—"),
                  },
                  { key: "datum", header: t("financieel.kolom.datum"),
                    cell: (b) => <span className="tabular">{formatDatum(b.datum, locale)}</span> },
                  { key: "relatie", header: t("financieel.kolom.relatie"), cell: (b) => b.klantNaam || "—" },
                  { key: "regel", header: t("omzet.boekingen.regel"),
                    cell: (b) => b.omschrijving || b.productNaam || "—" },
                  {
                    key: "grootboek", header: t("omzet.kolom.grootboek"),
                    cell: (b) => (b.grootboek
                      ? [b.grootboek, b.grootboekOmschrijving].filter(Boolean).join(" — ")
                      : t("omzet.zonderRekening")),
                  },
                  { key: "exclBtw", header: t("omzet.kolom.exclBtw"),
                    cell: (b) => <span className="is-num tabular">{formatEuro(b.exclBtw, locale)}</span> },
                  { key: "btw", header: t("omzet.kolom.btw"),
                    cell: (b) => <span className="is-num tabular">{formatEuro(b.btw, locale)}</span> },
                  { key: "inclBtw", header: t("omzet.kolom.inclBtw"),
                    cell: (b) => <span className="is-num tabular">{formatEuro(b.inclBtw, locale)}</span> },
                ]}
                rows={boekingen}
                getRowKey={(b) => String(b.regelId)}
                empty=""
              />
              {boekingenTotaal > BOEKINGEN_PER_PAGINA && (
                <div className="fawkes-fin__pager">
                  <Button
                    variant="secondary" disabled={boekingenPagina <= 1}
                    onClick={() => setBoekingenPagina((p) => Math.max(1, p - 1))}
                  >
                    {t("common.previous")}
                  </Button>
                  <span className="fawkes-fin__pageinfo">
                    {t("financieel.pagina", {
                      args: [String(boekingenPagina), String(boekingenPaginas)],
                    })}
                  </span>
                  <Button
                    variant="secondary" disabled={boekingenPagina >= boekingenPaginas}
                    onClick={() => setBoekingenPagina((p) => p + 1)}
                  >
                    {t("common.next")}
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {!isLoading && !isError && !analyse && boekingenVoor == null && (
        <div className="fawkes-omzet__layout">
          <aside className="fawkes-omzet__blok">
            <h3><BarChart3 size={16} aria-hidden="true" /> {t("omzet.perMaand")}</h3>
            <div className="fawkes-omzet__chart">
              <Trend punten={perMaand} locale={locale} />
            </div>
            <div className="fawkes-omzet__chart-actie">
              <Button variant="secondary" onClick={() => setAnalyse(true)}>
                <BarChart3 size={14} /> {t("omzet.analyse")}
              </Button>
            </div>
          </aside>

          <div className="fawkes-fin__tablewrap">
            {rijen.length === 0 ? (
              <p className="fawkes-fin__state">{t("omzet.leeg")}</p>
            ) : (
              <IndeelbareTabel
                tabelKey="omzet-groepen"
                columns={[
                  {
                    key: "groep", header: t(`omzet.kolom.${groeperen}`),
                    cell: (r) => (
                      groeperen === "grootboek" && r.sleutel !== ZONDER_REKENING ? (
                        <button
                          type="button" className="fawkes-fin__link"
                          onClick={() => { setGrootboek(r.sleutel); setAnalyse(true); }}
                        >
                          {r.sleutel}
                        </button>
                      ) : r.sleutel === ZONDER_REKENING ? (
                        t("omzet.zonderRekening")
                      ) : groeperen === "maand" ? maandLabel(r.sleutel, locale) : r.sleutel
                    ),
                  },
                  // De omschrijving hoort alleen bij een grootboekindeling; bij maand of vestiging
                  // bestaat hij niet.
                  ...(groeperen === "grootboek" ? [{
                    key: "omschrijving", header: t("omzet.kolom.omschrijving"),
                    cell: (r: typeof rijen[number]) => r.omschrijving || "—",
                  }] : []),
                  { key: "vestiging", header: t("financieel.kolom.vestiging"), cell: (r) => r.vestiging || "—" },
                  { key: "onderdeel", header: t("financieel.kolom.onderdeel"), cell: (r) => r.onderdeel || "—" },
                  ...(weergave !== "incl" ? [{
                    key: "exclBtw", header: t("omzet.kolom.exclBtw"),
                    cell: (r: typeof rijen[number]) => (
                      <span className="is-num tabular">{formatEuro(r.exclBtw, locale)}</span>
                    ),
                  }] : []),
                  ...(weergave === "beide" ? [{
                    key: "btw", header: t("omzet.kolom.btw"),
                    cell: (r: typeof rijen[number]) => (
                      <span className="is-num tabular">{formatEuro(r.btw, locale)}</span>
                    ),
                  }] : []),
                  ...(weergave !== "excl" ? [{
                    key: "inclBtw", header: t("omzet.kolom.inclBtw"),
                    cell: (r: typeof rijen[number]) => (
                      <span className="is-num tabular">{formatEuro(r.inclBtw, locale)}</span>
                    ),
                  }] : []),
                  {
                    key: "aantal", header: t("omzet.kolom.aantal"),
                    cell: (r) => (
                      <span className="is-num tabular">
                        {/* Het aantal opent de boekingen erachter: een getal op een geldscherm hoort
                            terug te voeren te zijn op de regels waaruit het bestaat. */}
                        <button
                          type="button" className="fawkes-fin__link"
                          onClick={() => { setBoekingenVoor(r.sleutel); setBoekingenPagina(1); }}
                        >
                          {r.aantal}
                        </button>
                      </span>
                    ),
                  },
                  {
                    key: "acties", header: "", vast: true,
                    cell: (r) => (
                      <div className="fawkes-fin__rowmenu">
                        <button
                          type="button" className="fawkes-fin__kebab" title={t("common.more")}
                          aria-haspopup="true" aria-expanded={menuVoor === r.sleutel}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuVoor(menuVoor === r.sleutel ? null : r.sleutel);
                          }}
                        >
                          <MoreVertical size={16} />
                        </button>
                        {menuVoor === r.sleutel && (
                          <div className="fawkes-fin__rowmenu-items" role="menu">
                            <button
                              type="button" role="menuitem"
                              onClick={() => {
                                setMenuVoor(null);
                                if (groeperen === "grootboek") setGrootboek(r.sleutel);
                                setAnalyse(true);
                              }}
                            >
                              {t("omzet.actie.trend")}
                            </button>
                            <button
                              type="button" role="menuitem"
                              onClick={() => {
                                setMenuVoor(null);
                                setBoekingenVoor(r.sleutel);
                                setBoekingenPagina(1);
                              }}
                            >
                              {t("omzet.actie.boekingen")}
                            </button>
                          </div>
                        )}
                      </div>
                    ),
                  },
                ]}
                rows={rijen}
                getRowKey={(r) => String(r.sleutel)}
                empty=""
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The trend as inline SVG bars: one bar per month, scaled to the biggest month in the series, with the
 * amounts on the axis. No chart library — this is the comparison the eye makes here, and it stays readable
 * in both themes and in print.
 */
function Trend({ punten: alle, locale, groot }: { punten: MaandPunt[]; locale: string; groot?: boolean }) {
  const t = useTranslate();
  if (alle.length === 0) {
    return <p className="fawkes-fin__state">{t("omzet.leeg")}</p>;
  }
  // The card is 20rem wide: twenty years of months would be a wall of hairlines. It shows the last twelve
  // and says so; the full analysis draws the whole series.
  const limiet = groot ? 60 : 12;
  const punten = alle.length > limiet ? alle.slice(-limiet) : alle;
  const hoogste = punten.reduce((m, p) => Math.max(m, Math.abs(p.inclBtw)), 0) || 1;
  const hoogte = groot ? 220 : 120;
  return (
    <div className={`fawkes-omzet__trend${groot ? " is-groot" : ""}`}>
      <div className="fawkes-omzet__as" aria-hidden="true">
        <span>{formatEuro(hoogste, locale)}</span>
        <span>{formatEuro(hoogste / 2, locale)}</span>
        <span>{formatEuro(0, locale)}</span>
      </div>
      <ol className="fawkes-omzet__bars" style={{ height: `${hoogte}px` }}>
        {punten.map((p) => (
          <li key={p.maand}>
            <span
              className="fawkes-omzet__bar-staaf"
              style={{ height: `${Math.max(2, Math.round((Math.abs(p.inclBtw) / hoogste) * 100))}%` }}
              title={`${maandLabel(p.maand, locale)}: ${formatEuro(p.inclBtw, locale)} (${p.aantal})`}
            />
            <small>{maandKort(p.maand, locale)}</small>
          </li>
        ))}
      </ol>
      {alle.length > punten.length && (
        <p className="fawkes-omzet__trend-noot">
          {t("omzet.trendBeperkt", { args: [String(punten.length)] })}
        </p>
      )}
    </div>
  );
}

/** "2026-08" → "aug 2026"; anything unexpected stays as it came so no month is silently renamed. */
function maandLabel(sleutel: string, locale: string): string {
  const d = maandDatum(sleutel);
  return d ? new Intl.DateTimeFormat(locale, { month: "short", year: "numeric", timeZone: "UTC" }).format(d)
    : sleutel;
}

/** The axis label: month only — the year is in the block's period filter. */
function maandKort(sleutel: string, locale: string): string {
  const d = maandDatum(sleutel);
  return d ? new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" }).format(d) : sleutel;
}

function maandDatum(sleutel: string): Date | null {
  const m = /^(\d{4})-(\d{2})$/.exec(sleutel);
  return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)) : null;
}

function Kpi({ icon, value, label, accent }: {
  icon: React.ReactNode; value: string; label: string; accent?: boolean;
}) {
  return (
    <div className={`fawkes-fin__kpi${accent ? " is-accent" : ""}`}>
      <span className="fawkes-fin__kpi-icon" aria-hidden="true">{icon}</span>
      <span className="fawkes-fin__kpi-text">
        <strong>{value}</strong>
        <small>{label}</small>
      </span>
    </div>
  );
}

